import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, map } from "rxjs";
import type { PersonMatchInput, PossiblePersonMatch } from "../../services/person-match.service";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { AuthService } from "../auth/authServices";
import { ConfigService } from "src/app/services/config.service";

export interface EmailConflict {
  key: string;
  party: string;
  partyLabel: string;
  masterType: string;
  masterId: string;
  name: string;
  storedEncounterEmail: string;
  encounterEmail: string;
  masterEmail: string;
  digitalServiceStatus: string;
}

type CustomSort = {
  active: string;
  direction: string;
};
@Injectable({
  providedIn: "root",
})
export class CalendarService {
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private Auth: AuthService,
    private configService: ConfigService,
  ) {
    this.apiUrl = this.configService.get('apiUrl') + '/admin';
  }

  roleName = this.Auth.getRoleName();
  roleQuery = this.roleName
    ? `&roleName=${encodeURIComponent(this.roleName)}`
    : "";

  findPossiblePersonMatches(payload: PersonMatchInput): Observable<PossiblePersonMatch[]> {
    const roleName = this.Auth.getRoleName();
    const params = roleName ? new HttpParams().set("roleName", roleName) : new HttpParams();
    return this.http.post<{ status: string; data: { matches: PossiblePersonMatch[] } }>(`${this.apiUrl}/person/possible-matches`, payload, { withCredentials: true, params })
      .pipe(map(response => response.data.matches));
  }

  createEncounter(
    data: any,
    strikePanelFiles: File[],
    attorneyDemoFiles: File[]
  ): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const formData = new FormData();
    if (data.personMatchDecision) formData.append("personMatchDecision", JSON.stringify(data.personMatchDecision));
    if (data.selectedPartyReferences) formData.append("selectedPartyReferences", JSON.stringify(data.selectedPartyReferences));
    if (data.encounterCreationId) formData.append("encounterCreationId", data.encounterCreationId);
    if (data.emailConflictResolutions) formData.append("emailConflictResolutions", JSON.stringify(data.emailConflictResolutions));

    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("panelNumber", data.panelNumber);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("online", data.online);
    formData.append("meetingLink", data.meetingLink);
    formData.append("reqParty", data.reqParty);
    formData.append("evaluationMode", data.evaluationMode ?? "OFFLINE");
    formData.append("remoteEvaluationMeetingUrl", data.remoteEvaluationMeetingUrl ?? "");
    formData.append("evaluationDurationMinutes", String(data.evaluationDurationMinutes ?? ""));
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    formData.append("deposition_addressLine_1", data.deposition_addressLine_1);
    formData.append("deposition_addressLine_2", data.deposition_addressLine_2);
    formData.append("deposition_city", data.deposition_city);
    formData.append("deposition_state", data.deposition_state);
    formData.append("deposition_zip", data.deposition_zip);
    formData.append(
      "previousEncounter",
      JSON.stringify(data.previousEncounter)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    // formData.append("applicantDigitalService", data.applicantDigitalService);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("injuryDetails", JSON.stringify(data.injuryDetails));
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);
    formData.append("DeuAddress", data.DeuAddress);
    formData.append("DeuCity", data.DeuCity);
    formData.append("DeuState", data.DeuState);
    formData.append("DeuZip", data.DeuZip);
    formData.append("coverLetterDueDate", data.coverLetterDueDate);
    formData.append("declarationDueDate", data.declarationDueDate);
    formData.append("dateOfReciept", data.dateOfReciept);

    formData.append("defenseFirm", data.defenseFirm);
    formData.append("defenseName", data.defenseName);
    formData.append("defenseNamemiddle", data.defenseNamemiddle);
    formData.append("defenseNamelast", data.defenseNamelast);
    formData.append("defenseAddress", data.defenseAddress);
    formData.append("defenseAddress_2", data.defenseAddress_2);
    formData.append("defenseCity", data.defenseCity);
    formData.append("defenseState", data.defenseState);
    formData.append("defenseZip", data.defenseZip);
    formData.append("defensePhone", data.defensePhone);
    formData.append("defenseFax", data.defenseFax);
    formData.append("defenseEmail", data.defenseEmail);
    formData.append("defenseAssistantEmail", data.defenseAssistantEmail);
    formData.append(
      "defensemodeOfTransmission",
      data.defensemodeOfTransmission
    );
    formData.append("employerSelfRepresented", data.employerSelfRepresented);
    formData.append("isEmail_sent_3", data.isEmail_sent_3);
    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("insuranceCompany", data.insuranceCompany);
    formData.append("insuranceCompanyPhone", data.insuranceCompanyPhone);
    formData.append("payerId", data.payerId);
    // formData.append("companyPayerId", data.companyPayerId);
    formData.append(
      "insurancemodeOfTransmission",
      data.insurancemodeOfTransmission
    );
    formData.append("billingEmail", data.billingEmail);
    formData.append("billingFax", data.billingFax);
    formData.append("billingAddress", data.billingAddress);
    formData.append("billingReviewPhone", data.billingReviewPhone);
    formData.append("billingReviewEmail", data.billingReviewEmail);
    formData.append("adjuster", data.adjuster);
    formData.append("adjuster_middleName", data.adjuster_middleName);
    formData.append("adjuster_lastName", data.adjuster_lastName);
    formData.append("insuranceAddress", data.insuranceAddress);
    formData.append("insuranceAddress_2", data.insuranceAddress_2);
    formData.append("insuranceCity", data.insuranceCity);
    formData.append("insuranceState", data.insuranceState);
    formData.append("insuranceZip", data.insuranceZip);
    formData.append("insurancePhone", data.insurancePhone);
    formData.append("insuranceFax", data.insuranceFax);
    formData.append("insuranceEmail", data.insuranceEmail);
    formData.append("insuranceAssistant", data.insuranceAssistant);
    formData.append("insuranceClaimNo", data.insuranceClaimNo);
    formData.append("isEmail_sent_5", data.isEmail_sent_5);
    formData.append("draft", data.isDraft);
    formData.append("appointmentDateOnly", data.appointmentDateOnly);
    formData.append("appointmentTimeOnly", data.appointmentTimeOnly);

    if (strikePanelFiles && strikePanelFiles.length > 0) {
      strikePanelFiles.forEach((file: File) => {
        formData.append("strikePanelFiles", file, file.name);
      });
    }

    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }

    // Post the form data to the server
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/create_encounter?name=Create Encounter${roleQuery}`,
      formData
    );
  }

  createDraftEncounter(
    data: any,
    strikePanelFiles: File[],
    attorneyDemoFiles: File[]
  ): Observable<any> {
    const formData = new FormData();
    if (data.personMatchDecision) formData.append("personMatchDecision", JSON.stringify(data.personMatchDecision));

    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("panelNumber", data.panelNumber);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("online", data.online);
    formData.append("meetingLink", data.meetingLink);
    formData.append("reqParty", data.reqParty);
    formData.append("evaluationMode", data.evaluationMode ?? "OFFLINE");
    formData.append("remoteEvaluationMeetingUrl", data.remoteEvaluationMeetingUrl ?? "");
    formData.append("evaluationDurationMinutes", String(data.evaluationDurationMinutes ?? ""));
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    formData.append("deposition_addressLine_1", data.deposition_addressLine_1);
    formData.append("deposition_addressLine_2", data.deposition_addressLine_2);
    formData.append("deposition_city", data.deposition_city);
    formData.append("deposition_state", data.deposition_state);
    formData.append("deposition_zip", data.deposition_zip);
    formData.append(
      "previousEncounter",
      JSON.stringify(data.previousEncounter)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    // formData.append("applicantDigitalService", data.applicantDigitalService);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("injuryDetails", JSON.stringify(data.injuryDetails));
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);
    formData.append("DeuAddress", data.DeuAddress);
    formData.append("DeuCity", data.DeuCity);
    formData.append("DeuState", data.DeuState);
    formData.append("DeuZip", data.DeuZip);
    formData.append("coverLetterDueDate", data.coverLetterDueDate);
    formData.append("declarationDueDate", data.declarationDueDate);
    formData.append("dateOfReciept", data.dateOfReciept);

    formData.append("defenseFirm", data.defenseFirm);
    formData.append("defenseName", data.defenseName);
    formData.append("defenseNamemiddle", data.defenseNamemiddle);
    formData.append("defenseNamelast", data.defenseNamelast);
    formData.append("defenseAddress", data.defenseAddress);
    formData.append("defenseAddress_2", data.defenseAddress_2);
    formData.append("defenseCity", data.defenseCity);
    formData.append("defenseState", data.defenseState);
    formData.append("defenseZip", data.defenseZip);
    formData.append("defensePhone", data.defensePhone);
    formData.append("defenseFax", data.defenseFax);
    formData.append("defenseEmail", data.defenseEmail);
    formData.append("defenseAssistantEmail", data.defenseAssistantEmail);
    formData.append(
      "defensemodeOfTransmission",
      data.defensemodeOfTransmission
    );
    formData.append("employerSelfRepresented", data.employerSelfRepresented);
    formData.append("isEmail_sent_3", data.isEmail_sent_3);
    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("insuranceCompany", data.insuranceCompany);
    formData.append("insuranceCompanyPhone", data.insuranceCompanyPhone);
    formData.append("payerId", data.payerId);
    // formData.append("companyPayerId", data.companyPayerId);
    formData.append(
      "insurancemodeOfTransmission",
      data.insurancemodeOfTransmission
    );
    formData.append("billingEmail", data.billingEmail);
    formData.append("billingFax", data.billingFax);
    formData.append("billingAddress", data.billingAddress);
    formData.append("billingReviewPhone", data.billingReviewPhone);
    formData.append("billingReviewEmail", data.billingReviewEmail);
    formData.append("adjuster", data.adjuster);
    formData.append("adjuster_middleName", data.adjuster_middleName);
    formData.append("adjuster_lastName", data.adjuster_lastName);
    formData.append("insuranceAddress", data.insuranceAddress);
    formData.append("insuranceAddress_2", data.insuranceAddress_2);
    formData.append("insuranceCity", data.insuranceCity);
    formData.append("insuranceState", data.insuranceState);
    formData.append("insuranceZip", data.insuranceZip);
    formData.append("insurancePhone", data.insurancePhone);
    formData.append("insuranceFax", data.insuranceFax);
    formData.append("insuranceEmail", data.insuranceEmail);
    formData.append("insuranceAssistant", data.insuranceAssistant);
    formData.append("insuranceClaimNo", data.insuranceClaimNo);
    formData.append("isEmail_sent_5", data.isEmail_sent_5);
    formData.append("draft", data.isDraft);

    if (strikePanelFiles && strikePanelFiles.length > 0) {
      strikePanelFiles.forEach((file: File) => {
        formData.append("strikePanelFiles", file, file.name);
      });
    }

    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }

    // Post the form data to the server
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/create_draft_encounter`,
      formData
    );
  }

  updateEncounter(
    data: any,
    strikePanelFiles: File[],
    attorneyDemoFiles: File[],
    id: any
  ): Observable<any> {
    console.log(data.applicantAttorneymiddle ,'service file')
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const formData = new FormData();
    if (data.emailConflictResolutions) formData.append("emailConflictResolutions", JSON.stringify(data.emailConflictResolutions));
    if (data.personMatchDecision) formData.append("personMatchDecision", JSON.stringify(data.personMatchDecision));
    // formData.append("applicantID", data.applicantID);
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("panelNumber", data.panelNumber);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("online", data.online);
    formData.append("meetingLink", data.meetingLink);
    formData.append("reqParty", data.reqParty);
    formData.append("evaluationMode", data.evaluationMode ?? "OFFLINE");
    formData.append("remoteEvaluationMeetingUrl", data.remoteEvaluationMeetingUrl ?? "");
    formData.append("evaluationDurationMinutes", String(data.evaluationDurationMinutes ?? ""));
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    formData.append("deposition_addressLine_1", data.deposition_addressLine_1);
    formData.append("deposition_addressLine_2", data.deposition_addressLine_2);
    formData.append("deposition_city", data.deposition_city);
    formData.append("deposition_state", data.deposition_state);
    formData.append("deposition_zip", data.deposition_zip);
    formData.append(
      "previousEncounter",
      JSON.stringify(data.previousEncounter)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    // formData.append("applicantDigitalService", data.applicantDigitalService);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("injuryDetails", JSON.stringify(data.injuryDetails));
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    // formData.append(
    //   "applicantAttorneyDigitalService",
    //   data.applicantAttorneyDigitalService
    // );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);
    formData.append("DeuAddress", data.DeuAddress);
    formData.append("DeuCity", data.DeuCity);
    formData.append("DeuState", data.DeuState);
    formData.append("DeuZip", data.DeuZip);
    formData.append("coverLetterDueDate", data.coverLetterDueDate);
    formData.append("declarationDueDate", data.declarationDueDate);
    formData.append("dateOfReciept", data.dateOfReciept);

    formData.append("defenseFirm", data.defenseFirm);
    formData.append("defenseName", data.defenseName);
    formData.append("defenseNamemiddle", data.defenseNamemiddle);
    formData.append("defenseNamelast", data.defenseNamelast);
    formData.append("defenseAddress", data.defenseAddress);
    formData.append("defenseAddress_2", data.defenseAddress_2);
    formData.append("defenseCity", data.defenseCity);
    formData.append("defenseState", data.defenseState);
    formData.append("defenseZip", data.defenseZip);
    formData.append("defensePhone", data.defensePhone);
    formData.append("defenseFax", data.defenseFax);
    formData.append("defenseEmail", data.defenseEmail);
    formData.append("defenseAssistantEmail", data.defenseAssistantEmail);
    formData.append(
      "defensemodeOfTransmission",
      data.defensemodeOfTransmission
    );
    formData.append("employerSelfRepresented", data.employerSelfRepresented);
    formData.append("isEmail_sent_3", data.isEmail_sent_3);
    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("insuranceCompany", data.insuranceCompany);
    formData.append("insuranceCompanyPhone", data.insuranceCompanyPhone);
    formData.append("payerId", data.payerId);
    // formData.append("companyPayerId", data.companyPayerId);
    formData.append(
      "insurancemodeOfTransmission",
      data.insurancemodeOfTransmission
    );
    formData.append("billingEmail", data.billingEmail);
    formData.append("billingFax", data.billingFax);
    formData.append("billingAddress", data.billingAddress);
    formData.append("billingReviewPhone", data.billingReviewPhone);
    formData.append("billingReviewEmail", data.billingReviewEmail);
    formData.append("adjuster", data.adjuster);
    formData.append("adjuster_middleName", data.adjuster_middleName);
    formData.append("adjuster_lastName", data.adjuster_lastName);
    formData.append("insuranceAddress", data.insuranceAddress);
    formData.append("insuranceAddress_2", data.insuranceAddress_2);
    formData.append("insuranceCity", data.insuranceCity);
    formData.append("insuranceState", data.insuranceState);
    formData.append("insuranceZip", data.insuranceZip);
    formData.append("insurancePhone", data.insurancePhone);
    formData.append("insuranceFax", data.insuranceFax);
    formData.append("insuranceEmail", data.insuranceEmail);
    formData.append("insuranceAssistant", data.insuranceAssistant);
    formData.append("insuranceClaimNo", data.insuranceClaimNo);
    formData.append("isEmail_sent_5", data.isEmail_sent_5);
    formData.append("draft", data.isDraft);
    formData.append("appointmentDateOnly", data.appointmentDateOnly);
    formData.append("appointmentTimeOnly", data.appointmentTimeOnly);
    strikePanelFiles.forEach((file: File, index: number) => {
      formData.append(`strikePanelFiles`, file, file.name);
    });

    attorneyDemoFiles.forEach((file: File, index: number) => {
      formData.append(`attorneyDemoFiles`, file, file.name);
    });

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/update_encounter/${id}?name=Update Encounter${roleQuery}`,
      formData
    );
  }

  updateRescheduledEncounter(
    data: any,
    strikePanelFiles: File[],
    attorneyDemoFiles: File[],
    id: any
  ): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const formData = new FormData();
    if (data.personMatchDecision) formData.append("personMatchDecision", JSON.stringify(data.personMatchDecision));
    // formData.append("applicantID", data.applicantID);
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("panelNumber", data.panelNumber);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("online", data.online);
    formData.append("meetingLink", data.meetingLink);
    formData.append("reqParty", data.reqParty);
    formData.append("evaluationMode", data.evaluationMode ?? "OFFLINE");
    formData.append("remoteEvaluationMeetingUrl", data.remoteEvaluationMeetingUrl ?? "");
    formData.append("evaluationDurationMinutes", String(data.evaluationDurationMinutes ?? ""));
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    formData.append("deposition_addressLine_1", data.deposition_addressLine_1);
    formData.append("deposition_addressLine_2", data.deposition_addressLine_2);
    formData.append("deposition_city", data.deposition_city);
    formData.append("deposition_state", data.deposition_state);
    formData.append("deposition_zip", data.deposition_zip);
    formData.append(
      "previousEncounter",
      JSON.stringify(data.previousEncounter)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    // formData.append("applicantDigitalService", data.applicantDigitalService);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("injuryDetails", JSON.stringify(data.injuryDetails));
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    // formData.append(
    //   "applicantAttorneyDigitalService",
    //   data.applicantAttorneyDigitalService
    // );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);
    formData.append("DeuAddress", data.DeuAddress);
    formData.append("DeuCity", data.DeuCity);
    formData.append("DeuState", data.DeuState);
    formData.append("DeuZip", data.DeuZip);
    formData.append("coverLetterDueDate", data.coverLetterDueDate);
    formData.append("declarationDueDate", data.declarationDueDate);
    formData.append("dateOfReciept", data.dateOfReciept);

    formData.append("defenseFirm", data.defenseFirm);
    formData.append("defenseName", data.defenseName);
    formData.append("defenseNamemiddle", data.defenseNamemiddle);
    formData.append("defenseNamelast", data.defenseNamelast);
    formData.append("defenseAddress", data.defenseAddress);
    formData.append("defenseAddress_2", data.defenseAddress_2);
    formData.append("defenseCity", data.defenseCity);
    formData.append("defenseState", data.defenseState);
    formData.append("defenseZip", data.defenseZip);
    formData.append("defensePhone", data.defensePhone);
    formData.append("defenseFax", data.defenseFax);
    formData.append("defenseEmail", data.defenseEmail);
    formData.append("defenseAssistantEmail", data.defenseAssistantEmail);
    formData.append(
      "defensemodeOfTransmission",
      data.defensemodeOfTransmission
    );
    formData.append("employerSelfRepresented", data.employerSelfRepresented);
    formData.append("isEmail_sent_3", data.isEmail_sent_3);
    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("insuranceCompany", data.insuranceCompany);
    formData.append("insuranceCompanyPhone", data.insuranceCompanyPhone);
    formData.append("payerId", data.payerId);
    // formData.append("companyPayerId", data.companyPayerId);
    formData.append(
      "insurancemodeOfTransmission",
      data.insurancemodeOfTransmission
    );
    formData.append("billingEmail", data.billingEmail);
    formData.append("billingFax", data.billingFax);
    formData.append("billingAddress", data.billingAddress);
    formData.append("billingReviewPhone", data.billingReviewPhone);
    formData.append("billingReviewEmail", data.billingReviewEmail);
    formData.append("adjuster", data.adjuster);
    formData.append("adjuster_middleName", data.adjuster_middleName);
    formData.append("adjuster_lastName", data.adjuster_lastName);
    formData.append("insuranceAddress", data.insuranceAddress);
    formData.append("insuranceAddress_2", data.insuranceAddress_2);
    formData.append("insuranceCity", data.insuranceCity);
    formData.append("insuranceState", data.insuranceState);
    formData.append("insuranceZip", data.insuranceZip);
    formData.append("insurancePhone", data.insurancePhone);
    formData.append("insuranceFax", data.insuranceFax);
    formData.append("insuranceEmail", data.insuranceEmail);
    formData.append("insuranceAssistant", data.insuranceAssistant);
    formData.append("insuranceClaimNo", data.insuranceClaimNo);
    formData.append("isEmail_sent_5", data.isEmail_sent_5);
    formData.append("draft", "false");
    formData.append("appointmentDateOnly", data.appointmentDateOnly);
    formData.append("appointmentTimeOnly", data.appointmentTimeOnly);
    strikePanelFiles.forEach((file: File, index: number) => {
      formData.append(`strikePanelFiles`, file, file.name);
    });

    attorneyDemoFiles.forEach((file: File, index: number) => {
      formData.append(`attorneyDemoFiles`, file, file.name);
    });

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/update_rescheduled_encounter/${id}`,
      formData
    );
  }

  updateDraftEncounter(
    data: any,
    strikePanelFiles: File[],
    attorneyDemoFiles: File[],
    id: any
  ): Observable<any> {
    const formData = new FormData();
    if (data.personMatchDecision) formData.append("personMatchDecision", JSON.stringify(data.personMatchDecision));
    // formData.append("applicantID", data.applicantID);
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("panelNumber", data.panelNumber);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("online", data.online);
    formData.append("meetingLink", data.meetingLink);
    formData.append("reqParty", data.reqParty);
    formData.append("evaluationMode", data.evaluationMode ?? "OFFLINE");
    formData.append("remoteEvaluationMeetingUrl", data.remoteEvaluationMeetingUrl ?? "");
    formData.append("evaluationDurationMinutes", String(data.evaluationDurationMinutes ?? ""));
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    formData.append("deposition_addressLine_1", data.deposition_addressLine_1);
    formData.append("deposition_addressLine_2", data.deposition_addressLine_2);
    formData.append("deposition_city", data.deposition_city);
    formData.append("deposition_state", data.deposition_state);
    formData.append("deposition_zip", data.deposition_zip);
    formData.append(
      "previousEncounter",
      JSON.stringify(data.previousEncounter)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    // formData.append("applicantDigitalService", data.applicantDigitalService);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("injuryDetails", JSON.stringify(data.injuryDetails));
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    // formData.append(
    //   "applicantAttorneyDigitalService",
    //   data.applicantAttorneyDigitalService
    // );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);
    formData.append("DeuAddress", data.DeuAddress);
    formData.append("DeuCity", data.DeuCity);
    formData.append("DeuState", data.DeuState);
    formData.append("DeuZip", data.DeuZip);
    formData.append("coverLetterDueDate", data.coverLetterDueDate);
    formData.append("declarationDueDate", data.declarationDueDate);
    formData.append("dateOfReciept", data.dateOfReciept);

    formData.append("defenseFirm", data.defenseFirm);
    formData.append("defenseName", data.defenseName);
    formData.append("defenseNamemiddle", data.defenseNamemiddle);
    formData.append("defenseNamelast", data.defenseNamelast);
    formData.append("defenseAddress", data.defenseAddress);
    formData.append("defenseAddress_2", data.defenseAddress_2);
    formData.append("defenseCity", data.defenseCity);
    formData.append("defenseState", data.defenseState);
    formData.append("defenseZip", data.defenseZip);
    formData.append("defensePhone", data.defensePhone);
    formData.append("defenseFax", data.defenseFax);
    formData.append("defenseEmail", data.defenseEmail);
    formData.append("defenseAssistantEmail", data.defenseAssistantEmail);
    formData.append(
      "defensemodeOfTransmission",
      data.defensemodeOfTransmission
    );
    formData.append("isEmail_sent_3", data.isEmail_sent_3);
    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("insuranceCompany", data.insuranceCompany);
    formData.append("insuranceCompanyPhone", data.insuranceCompanyPhone);
    formData.append("payerId", data.payerId);
    // formData.append("companyPayerId", data.companyPayerId);
    formData.append(
      "insurancemodeOfTransmission",
      data.insurancemodeOfTransmission
    );
    formData.append("billingEmail", data.billingEmail);
    formData.append("billingFax", data.billingFax);
    formData.append("billingAddress", data.billingAddress);
    formData.append("billingReviewPhone", data.billingReviewPhone);
    formData.append("billingReviewEmail", data.billingReviewEmail);
    formData.append("adjuster", data.adjuster);
    formData.append("adjuster_middleName", data.adjuster_middleName);
    formData.append("adjuster_lastName", data.adjuster_lastName);
    formData.append("insuranceAddress", data.insuranceAddress);
    formData.append("insuranceAddress_2", data.insuranceAddress_2);
    formData.append("insuranceCity", data.insuranceCity);
    formData.append("insuranceState", data.insuranceState);
    formData.append("insuranceZip", data.insuranceZip);
    formData.append("insurancePhone", data.insurancePhone);
    formData.append("insuranceFax", data.insuranceFax);
    formData.append("insuranceEmail", data.insuranceEmail);
    formData.append("insuranceAssistant", data.insuranceAssistant);
    formData.append("insuranceClaimNo", data.insuranceClaimNo);
    formData.append("isEmail_sent_5", data.isEmail_sent_5);
    formData.append("draft", data.isDraft);
    strikePanelFiles.forEach((file: File, index: number) => {
      formData.append(`strikePanelFiles`, file, file.name);
    });

    attorneyDemoFiles.forEach((file: File, index: number) => {
      formData.append(`attorneyDemoFiles`, file, file.name);
    });

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/update_draft_encounter/${id}`,
      formData
    );
  }

  // SIBTF

  createSIBTFEncounter(data: any, attorneyDemoFiles: File[]): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const formData = new FormData();
    if (data.selectedPartyReferences) formData.append("selectedPartyReferences", JSON.stringify(data.selectedPartyReferences));
    if (data.encounterCreationId) formData.append("encounterCreationId", data.encounterCreationId);
    if (data.emailConflictResolutions) formData.append("emailConflictResolutions", JSON.stringify(data.emailConflictResolutions));
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    
    const previousEncounterData = data.previousEncounter || [];
    console.log("createSIBTFEncounter - previousEncounter data being sent:", previousEncounterData);
    console.log("createSIBTFEncounter - previousEncounter length:", previousEncounterData.length);
    if (previousEncounterData.length > 0) {
      previousEncounterData.forEach((enc: any, index: number) => {
        console.log(`Previous encounter ${index} being sent:`, {
          previousDate: enc.previousDate,
          evaluationType: enc.evaluationType,
          encounterType: enc.encounterType,
          status: enc.status
        });
      });
    } else {
      console.log("⚠️ createSIBTFEncounter - previousEncounter is EMPTY!");
    }
    
    formData.append(
      "previousEncounter",
      JSON.stringify(previousEncounterData)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);

    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("draft", data.isDraft);
    formData.append("sibtf", data.sibtf);
    formData.append("subFund", data.subFund);
    formData.append("subPhone", data.subPhone);
    formData.append("subFax", data.subFax);
    formData.append("subAddress", data.subAddress);
    formData.append("subAddress_2", data.subAddress_2);
    formData.append("subCity", data.subCity);
    formData.append("subState", data.subState);
    formData.append("subZip", data.subZip);
    formData.append("subEmail", data.subEmail);
    formData.append("appointmentDateOnly", data.appointmentDateOnly);
    formData.append("appointmentTimeOnly", data.appointmentTimeOnly);
    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }

    // Post the form data to the server
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/create_encounter?name=Create Encounter${roleQuery}`,
      formData
    );
  }

  updateSIBTFEncounter(
    data: any,
    attorneyDemoFiles: File[],
    id: any
  ): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    console.log("updateSIBTFEncounter - data:", data);
    const formData = new FormData();
    if (data.emailConflictResolutions) formData.append("emailConflictResolutions", JSON.stringify(data.emailConflictResolutions));
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    
    const previousEncounterData = data.previousEncounter || [];
    console.log("updateSIBTFEncounter - previousEncounter data being sent:", previousEncounterData);
    console.log("updateSIBTFEncounter - previousEncounter length:", previousEncounterData.length);
    if (previousEncounterData.length > 0) {
      previousEncounterData.forEach((enc: any, index: number) => {
        console.log(`Previous encounter ${index} being sent:`, {
          previousDate: enc.previousDate,
          evaluationType: enc.evaluationType,
          encounterType: enc.encounterType,
          status: enc.status
        });
      });
    } else {
      console.log("⚠️ updateSIBTFEncounter - previousEncounter is EMPTY!");
    }
    
    formData.append(
      "previousEncounter",
      JSON.stringify(previousEncounterData)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);

    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("draft", data.isDraft);
    formData.append("sibtf", data.sibtf);
    formData.append("subFund", data.subFund);
    formData.append("subPhone", data.subPhone);
    formData.append("subFax", data.subFax);
    formData.append("subAddress", data.subAddress);
    formData.append("subAddress_2", data.subAddress_2);
    formData.append("subCity", data.subCity);
    formData.append("subState", data.subState);
    formData.append("subZip", data.subZip);
    formData.append("subEmail", data.subEmail);
    formData.append("appointmentDateOnly", data.appointmentDateOnly);
    formData.append("appointmentTimeOnly", data.appointmentTimeOnly);
    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/update_encounter/${id}?name=Update Encounter${roleQuery}`,
      formData
    );
  }

  createDraftSIBTFEncounter(
    data: any,
    attorneyDemoFiles: File[]
  ): Observable<any> {
    const formData = new FormData();
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    
    const previousEncounterData = data.previousEncounter || [];
    console.log("createSIBTFEncounter - previousEncounter data being sent:", previousEncounterData);
    console.log("createSIBTFEncounter - previousEncounter length:", previousEncounterData.length);
    if (previousEncounterData.length > 0) {
      previousEncounterData.forEach((enc: any, index: number) => {
        console.log(`Previous encounter ${index} being sent:`, {
          previousDate: enc.previousDate,
          evaluationType: enc.evaluationType,
          encounterType: enc.encounterType,
          status: enc.status
        });
      });
    } else {
      console.log("⚠️ createSIBTFEncounter - previousEncounter is EMPTY!");
    }
    
    formData.append(
      "previousEncounter",
      JSON.stringify(previousEncounterData)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);

    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("draft", data.isDraft);
    formData.append("sibtf", data.sibtf);
    formData.append("subFund", data.subFund);
    formData.append("subPhone", data.subPhone);
    formData.append("subFax", data.subFax);
    formData.append("subAddress", data.subAddress);
    formData.append("subAddress_2", data.subAddress_2);
    formData.append("subCity", data.subCity);
    formData.append("subState", data.subState);
    formData.append("subZip", data.subZip);
    formData.append("subEmail", data.subEmail);

    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }

    // Post the form data to the server
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/create_draft_encounter`,
      formData
    );
  }

  updateDraftSIBTFEncounter(
    data: any,
    attorneyDemoFiles: File[],
    id: any
  ): Observable<any> {
    const formData = new FormData();
    if (data.organizationId) formData.append("organizationId", data.organizationId);
    formData.append("physician", data.physician);
    formData.append("medicalEvaluationType", data.medicalEvaluationType);
    formData.append("evaluationType", data.evaluationType);
    formData.append("encounterType", data.encounterType);
    formData.append("dateTimeOfAppointment", data.dateTimeOfAppointment);
    formData.append("timeOfAppointment", data.timeOfAppointment);
    formData.append("dateOfAppointment", data.dateOfAppointment);
    formData.append("dateOfAppointmentCall", data.dateOfAppointmentCall);
    formData.append("examinationAddress", data.examinationAddress);
    formData.append("examinationCity", data.examinationCity);
    formData.append("examinationState", data.examinationState);
    formData.append("examinationZip", data.examinationZip);
    
    const previousEncounterData = data.previousEncounter || [];
    console.log("createSIBTFEncounter - previousEncounter data being sent:", previousEncounterData);
    console.log("createSIBTFEncounter - previousEncounter length:", previousEncounterData.length);
    if (previousEncounterData.length > 0) {
      previousEncounterData.forEach((enc: any, index: number) => {
        console.log(`Previous encounter ${index} being sent:`, {
          previousDate: enc.previousDate,
          evaluationType: enc.evaluationType,
          encounterType: enc.encounterType,
          status: enc.status
        });
      });
    } else {
      console.log("⚠️ createSIBTFEncounter - previousEncounter is EMPTY!");
    }
    
    formData.append(
      "previousEncounter",
      JSON.stringify(previousEncounterData)
    );
    formData.append("applicantName", data.applicantName);
    formData.append("applicantMiddleName", data.applicantMiddleName);
    formData.append("applicantLastName", data.applicantLastName);
    formData.append("dob", data.dob);
    formData.append("ssn", data.ssn);
    formData.append("gender", data.gender);
    formData.append("address", data.address);
    formData.append("address_2", data.address_2);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("zip", data.zip);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("altPhone", data.altPhone);
    formData.append("workPhone", data.workPhone);
    formData.append("jobtitle", data.jobtitle);
    formData.append("modeOfTransmission", data.modeOfTransmission);
    formData.append("isEmail_sent", data.isEmail_sent);
    formData.append("interpreter", data.interpreter);
    formData.append("language", data.language);
    formData.append("vendor", data.vendor);
    formData.append("vendorPhone", data.vendorPhone);
    formData.append("unrepresented", data.unrepresented);
    formData.append("firm", data.firm);
    formData.append("applicantAttorneyname", data.applicantAttorneyname);
    formData.append("applicantAttorneymiddle", data.applicantAttorneymiddle);
    formData.append("applicantAttorneylast", data.applicantAttorneylast);
    formData.append("applicantAttorneyaddress", data.applicantAttorneyaddress);
    formData.append(
      "applicantAttorneyaddress_2",
      data.applicantAttorneyaddress_2
    );
    formData.append("applicantAttorneyCity", data.applicantAttorneyCity);
    formData.append("applicantAttorneyState", data.applicantAttorneyState);
    formData.append("applicantAttorneyZip", data.applicantAttorneyZip);
    formData.append(
      "applicantAttorneyTelePhone",
      data.applicantAttorneyTelePhone
    );
    formData.append("applicantAttorneyFax", data.applicantAttorneyFax);
    formData.append("applicantAttorneyEmail", data.applicantAttorneyEmail);
    formData.append(
      "applicantAttorneyAssitantEmail",
      data.applicantAttorneyAssitantEmail
    );
    formData.append(
      "applicantAttorneymodeOfTransmission",
      data.applicantAttorneymodeOfTransmission
    );
    formData.append("isEmail_sent_2", data.isEmail_sent_2);

    formData.append("employer", data.employer);
    formData.append("employerPhone", data.employerPhone);
    formData.append("employerFax", data.employerFax);
    formData.append("employerEmail", data.employerEmail);
    formData.append("employerAddress", data.employerAddress);
    formData.append("employerAddress_2", data.employerAddress_2);
    formData.append("employerCity", data.employerCity);
    formData.append("employerState", data.employerState);
    formData.append("employerZip", data.employerZip);
    formData.append(
      "employermodeOfTransmission",
      data.employermodeOfTransmission
    );
    formData.append("isEmail_sent_4", data.isEmail_sent_4);
    formData.append("draft", data.isDraft);
    formData.append("sibtf", data.sibtf);
    formData.append("subFund", data.subFund);
    formData.append("subPhone", data.subPhone);
    formData.append("subFax", data.subFax);
    formData.append("subAddress", data.subAddress);
    formData.append("subAddress_2", data.subAddress_2);
    formData.append("subCity", data.subCity);
    formData.append("subState", data.subState);
    formData.append("subZip", data.subZip);
    formData.append("subEmail", data.subEmail);

    if (attorneyDemoFiles && attorneyDemoFiles.length > 0) {
      attorneyDemoFiles.forEach((file: File) => {
        formData.append("attorneyDemoFiles", file, file.name);
      });
    }
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/update_draft_encounter/${id}`,
      formData
    );
  }

  getEncounterById(id: string) {
    return this.http.get<{
      data: any;
    }>(`${this.apiUrl}/get_encounter/${id}`);
  }

  getUserCalendar(calendarId: string): Observable<{
    calendars: Array<{ calendarId: string; calendarName: string }>;
  }> {
    return this.http.get<{
      calendars: Array<{ calendarId: string; calendarName: string }>;
    }>(
      `${this.apiUrl}/get/userCalendar/${encodeURIComponent(calendarId)}`,
      { params: { roleName: this.Auth.getRoleName() || "" } }
    );
  }

  getPhysicianExaminationAddressById(physicianId: string) {
    return this.http.get<{
      data: any;
    }>(`${this.apiUrl}/clinic_locations/physician/${physicianId}`);
  }

  /** Active insurance company names for filter dropdowns */
  getInsuranceComp() {
    return this.http.get<{
      data: any;
    }>(
      `${this.apiUrl}/getAll_insuranceCompany_details?view=names&active=true&all=true`
    );
  }



  getInsuranceComp_2(
    search: string,
    page: number,
    limit: number,
    companyExact?: string,
    caseType?: string
  ) {
    const params = new URLSearchParams({
      view: "autofill",
      active: "true",
      page: String(page),
      limit: String(limit),
    });
    if (companyExact && companyExact.trim()) {
      params.set("companyExact", companyExact.trim());
    } else if (search && search.trim()) {
      params.set("search", search.trim());
    }
    if (caseType && caseType.trim()) {
      params.set("caseType", caseType.trim());
    }
    return this.http.get<{ message: string; data: any; totalPages?: number; currentPage?: number }>(
      `${this.apiUrl}/getAll_insuranceCompany_details?${params.toString()}`
    );
  }

  getMasterDigitalServiceStatus(body: Record<string, unknown>) {
    return this.http.post<{ status: string; email: string | null }>(
      `${this.apiUrl}/digital-service/master-status`,
      body
    );
  }

  /** Active insurance company names for filter dropdowns */
  getallInsuranceComp() {
    return this.http.get<{
      data: any;
    }>(
      `${this.apiUrl}/getAll_insuranceCompany_details?view=names&active=true&all=true`
    );
  }

  getPhysicians(organizationId?: string): Observable<any> {
    return this.http.get<{ status: string; results: number; data: any }>(
      `${this.apiUrl}/getPhysician${organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : ""}`
    );
  }

  getPreferredCalendarPhysicians(): Observable<{
    status: string;
    data: { preferredCalendarPhysicians: string[] };
  }> {
    return this.http.get<{
      status: string;
      data: { preferredCalendarPhysicians: string[] };
    }>(`${this.apiUrl}/preferredCalendarPhysicians`);
  }

  updatePreferredCalendarPhysicians(
    preferredCalendarPhysicians: string[]
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.apiUrl}/updatePreferredCalendarPhysicians`,
      { preferredCalendarPhysicians }
    );
  }

  checkPhysicianSignature(id: string): Observable<{
    success: boolean;
    available: boolean;
    calendarLinked?: boolean;
    calendarWritable?: boolean;
    teamEmailPresent?: boolean;
  }> {
    return this.http.get<{
      success: boolean;
      available: boolean;
      calendarLinked?: boolean;
      calendarWritable?: boolean;
      teamEmailPresent?: boolean;
    }>(`${this.apiUrl}/physician/${id}/check-signature`);
  }

  checkQmeCalendarIntegration(calendarId: string): Observable<{
    success: boolean;
    integrationOwnerEmail: string;
    integrationWritable: boolean;
    checked: boolean;
  }> {
    return this.http.get<{
      success: boolean;
      integrationOwnerEmail: string;
      integrationWritable: boolean;
      checked: boolean;
    }>(
      `${this.apiUrl}/check-qme-calendar-integration/${encodeURIComponent(calendarId)}`
    );
  }

  getAllApplicants(): Observable<any> {
    return this.http.get<{ status: string; data: any }>(
      `${this.apiUrl}/get_applicant`
    );
  }

  getAllApplicants_(search: string, page: number, limit: number, partyRecordType = "applicant") {
    let searchQuery = "";
    if (search && search.trim()) {
      searchQuery = `&search=${encodeURIComponent(search.trim())}`;
    }
    const partyQuery = partyRecordType
      ? `&partyRecordType=${encodeURIComponent(partyRecordType)}`
      : "";
    return this.http.get<{ message: string; data: any }>(
      `${this.apiUrl}/get_Allapplicants?${searchQuery}&active=true&page=${page}&limit=${limit}${partyQuery}`
    );
  }

  getAllAttorney_AA(
    search: any,
    page: number,
    limit: number,
    firmExact?: string
  ) {
    let searchQuery = "";
    if (firmExact && String(firmExact).trim()) {
      searchQuery = `&firmExact=${encodeURIComponent(String(firmExact).trim())}`;
    } else if (search && search.trim()) {
      searchQuery = `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<{
      message: string;
      data: any;
      totalPages?: number;
      currentPage?: number;
    }>(
      `${this.apiUrl}/getAll_Attorney/details?active=true&attorneyType=AA${searchQuery}${this.roleQuery}&page=${page}&limit=${limit}`
    );
  }

  getAllAttorney_DA(
    search: any,
    page: number,
    limit: number,
    firmExact?: string
  ) {
    let searchQuery = "";
    if (firmExact && String(firmExact).trim()) {
      searchQuery = `&firmExact=${encodeURIComponent(String(firmExact).trim())}`;
    } else if (search && search.trim()) {
      searchQuery = `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<{
      message: string;
      data: any;
      totalPages?: number;
      currentPage?: number;
    }>(
      `${this.apiUrl}/getAll_Attorney/details?active=true&attorneyType=DA${searchQuery}${this.roleQuery}&page=${page}&limit=${limit}`
    );
  }

  /** Personal Injury plaintiff attorneys only. */
  getAllAttorney_AA_PI(
    search: any,
    page: number,
    limit: number,
    firmExact?: string
  ) {
    let searchQuery = "";
    if (firmExact && String(firmExact).trim()) {
      searchQuery = `&firmExact=${encodeURIComponent(String(firmExact).trim())}`;
    } else if (search && search.trim()) {
      searchQuery = `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<{
      message: string;
      data: any;
      totalPages?: number;
      currentPage?: number;
    }>(
      `${this.apiUrl}/getAll_Attorney/details?active=true&attorneyType=${encodeURIComponent(
        "AA (PI)"
      )}${searchQuery}${this.roleQuery}&page=${page}&limit=${limit}`
    );
  }

  /** Personal Injury defense attorneys — DA (PI). */
  getAllAttorney_DA_PI(
    search: any,
    page: number,
    limit: number,
    firmExact?: string
  ) {
    let searchQuery = "";
    if (firmExact && String(firmExact).trim()) {
      searchQuery = `&firmExact=${encodeURIComponent(String(firmExact).trim())}`;
    } else if (search && search.trim()) {
      searchQuery = `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<{
      message: string;
      data: any;
      totalPages?: number;
      currentPage?: number;
    }>(
      `${this.apiUrl}/getAll_Attorney/details?active=true&attorneyType=${encodeURIComponent(
        "DA (PI)"
      )}${searchQuery}${this.roleQuery}&page=${page}&limit=${limit}`
    );
  }

  getAllAttorney(): Observable<any> {
    return this.http.get<{ status: string; data: any }>(
      `${this.apiUrl}/getAll_Attorney/details`
    );
  }

  delete(id: string): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    return this.http.post<any>(
      `${this.apiUrl}/delete_encounter/${id}?name=Delete Encounter${roleQuery}`,
      {}
    );
  }

  updateEncounterBasic(data: any, id: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/update_encounter_basic/${id}`,
      data
    );
  }

  getEncounters(physicianIds: string[] = []): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const params = physicianIds.length
      ? { physicianName: physicianIds.join(",") }
      : {};

    return this.http.get<{ status: string; results: number; data: any }>(
      `${this.apiUrl}/get_inprogress_encounters?name=View All Calendar Events${roleQuery}`,
      { params }
    );
  }

  getAllEncounters(
    page: number,
    limit: number,
    search: string = "",
    sortParams: CustomSort[] = []
  ): Observable<any> {
    const roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
    const sortQuery = sortParams
      .map((s) => `&sort[${s.active}]=${s.direction}`)
      .join("&");

    return this.http.get<{
      status: string;
      results: number;
      total?: number;
      data: any[];
    }>(
      `${this.apiUrl}/get_all_encounters?name=View Encounters&page=${page}&limit=${limit}${roleQuery}${searchQuery}${sortQuery}`
    );
  }

  getAllBodyParts(category: any): Observable<any> {
    return this.http.get<{ status: string; data: any }>(
      `${this.apiUrl}/getAll_bodyparts?category=${category}`
    );
  }

  getBodyPartCategories() {
    return this.http.get<{ status: string; data: { _id: string; name: string }[] }>(
      `${this.apiUrl}/getBodyPartCategories`
    );
  }

  getPDF(encounterData: any, type: any) {
    return this.http.post(
      `${this.apiUrl}/generate-pdf?recipientType=${type}`,
      { encounterData: encounterData },
      {
        responseType: "blob",
      }
    );
  }

  combineANFPdfs(pdfBuffersBase64: string[]) {
    return this.http.post(
      `${this.apiUrl}/combine-anf-pdfs`,
      { pdfBuffers: pdfBuffersBase64 },
      { responseType: "blob" }
    );
  }

  checkApplicantEncounter(
    applicantName: any,
    applicantLastName: any,
    dob: any,
    physician: any
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/checkApplicantEncounter`, {
      applicantName,
      applicantLastName,
      dob,
      physician,
    });
  }

  checkApplicantDateEncounter(
    applicantName: any,
    applicantLastName: any,
    dob: any,
    physician: any,
    checkDate: any
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/checkApplicantDateEncounter`, {
      applicantName,
      applicantLastName,
      dob,
      physician,
      checkDate,
    });
  }

  checkApplicantAppointmentDateEncounter(
    applicantName: any,
    applicantLastName: any,
    dob: any,
    physician: any,
    checkDate: any,
    excludeEncounterId?: any
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/checkApplicantAppointmentDateEncounter`,
      { applicantName, applicantLastName, dob, physician, checkDate, excludeEncounterId }
    );
  }

  getPreviousEncounterData(
    applicantName: any,
    applicantLastName: any,
    dob: any,
    physician: any
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/get_previousEncounterData`, {
      applicantName,
      applicantLastName,
      dob,
      physician,
    });
  }

  getAllDeuDetails(): Observable<any> {
    return this.http.get(`${this.apiUrl}/getDEU`);
  }

  getTasks(page: number = 1, limit: number = 1000): Observable<any> {
    let roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    const params = {
      page: page.toString(),
      limit: limit.toString()
    };
    return this.http.get<{ status: string; data: any }>(
      `${this.apiUrl}/getTask_details?name=View Tasks${roleQuery}`,
      { params }
    );
  }
}
